import api from './api';
import type { CuppingSession, CuppingSessionDetail, CuppingSessionStatus, CreateCuppingSessionPayload } from '../types/cuppingSession';
import { normalizeListResponse } from '../utils/listResponse';

interface CuppingSessionDetailDto {
  id: string;
  cupping_session_id: string;
  roasted_bulk_id: string;
  inventory_item_id?: string;
  lot_id?: string | null;
  work_order_id?: string | null;
  work_order_number?: string | null;
  item_name?: string | null;
  roast_type?: string | null;
  blind_code?: string | null;
  order_index?: number | null;
}

interface CuppingSessionDto {
  id: string;
  code: string;
  cupper_id: string;
  cupper_name?: string;
  status: CuppingSessionStatus | string;
  created_at: string;
  updated_at?: string;
  completed_at?: string | null;
  blind_mode?: boolean;
  cupping_mode?: string;
  details?: CuppingSessionDetailDto[];
}

const mapDetail = (dto: CuppingSessionDetailDto): CuppingSessionDetail => ({
  id: dto.id,
  cuppingSessionId: dto.cupping_session_id,
  roastedBulkId: dto.roasted_bulk_id,
  inventoryItemId: dto.inventory_item_id ?? dto.roasted_bulk_id,
  lotId: dto.lot_id ?? undefined,
  workOrderId: dto.work_order_id ?? undefined,
  workOrderNumber: dto.work_order_number ?? '-',
  itemName: dto.item_name ?? '-',
  roastType: dto.roast_type ?? '-',
  blindCode: dto.blind_code ?? undefined,
  orderIndex: dto.order_index ?? 0,
});

const mapSession = (dto: CuppingSessionDto): CuppingSession => ({
  id: dto.id,
  code: dto.code,
  cupperId: dto.cupper_id,
  cupperName: dto.cupper_name,
  status: dto.status === 'complete' ? 'Complete' : dto.status === 'draft' ? 'Draft' : (dto.status as CuppingSessionStatus),
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
  completedAt: dto.completed_at,
  blindMode: dto.blind_mode,
  cuppingMode: (dto.cupping_mode as CuppingSession['cuppingMode']) || 'Simplified',
  details: dto.details?.map(mapDetail),
});

export const cuppingSessionService = {
  getAll: async (params?: { search?: string; date?: string; status?: CuppingSessionStatus | 'all' }) => {
    const response = await api.get<CuppingSessionDto[]>('/cupping-sessions', {
      params: {
        search: params?.search || undefined,
        date: params?.date || undefined,
        status: params?.status && params.status !== 'all' ? params.status.toLowerCase() : undefined,
      },
    });
    return (response.data || []).map(mapSession);
  },

  list: async (params: { page?: number; size?: number; search?: string; date?: string; status?: CuppingSessionStatus | 'all' } = {}) => {
    const { page = 1, size = 25, search, date, status } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (search) query.set('search', search);
    if (date) query.set('date', date);
    if (status && status !== 'all') query.set('status', status.toLowerCase());
    const response = await api.get(`/cupping-sessions?${query.toString()}`);
    const normalized = normalizeListResponse<CuppingSessionDto>(response.data);
    return { data: normalized.data.map(mapSession), total: normalized.total };
  },
  getById: async (id: string) => {
    const response = await api.get<CuppingSessionDto>(`/cupping-sessions/${id}`);
    return mapSession(response.data);
  },
  create: async (payload: CreateCuppingSessionPayload) => {
    const response = await api.post<CuppingSessionDto>('/cupping-sessions', {
      code: payload.code,
      cupper_id: payload.cupperId,
      blind_mode: payload.blindMode,
      cupping_mode: payload.cuppingMode,
      onboarding_sample: payload.onboardingSample,
      details: payload.details.map((detail) => ({
        roasted_bulk_id: detail.roastedBulkId,
        inventory_item_id: detail.inventoryItemId,
        lot_id: detail.lotId,
        work_order_id: detail.workOrderId,
        work_order_number: detail.workOrderNumber,
        item_name: detail.itemName,
        roast_type: detail.roastType,
        blind_code: detail.blindCode,
        order_index: detail.orderIndex,
      })),
    });
    return mapSession(response.data);
  },
  complete: async (id: string) => {
    const response = await api.patch<CuppingSessionDto>(`/cupping-sessions/${id}`, {
      status: 'complete',
    });
    return mapSession(response.data);
  },
};
